import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import { NSModal, NSField, NSSelect, NSDrop, NSToggle } from '../shared/ns-kit'
import { I } from '../shared/ns-icons'
import '../../styles/admin-pages.css'

// ── SVG icons ─────────────────────────────────────────────────────────────────
function IconFolder({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}
function IconImage({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}
function IconFile({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  )
}
function IconVideo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  )
}
function IconSearch({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}
function IconUpload({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  )
}
function IconX({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
function IconTrash({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
  )
}
function IconLink({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )
}
function IconGrid({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}
function IconList({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function fileTypeLabel(mimeType = '') {
  if (mimeType.startsWith('image/')) return 'img'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.includes('pdf')) return 'pdf'
  if (mimeType.includes('svg')) return 'svg'
  return 'file'
}

const ALL_FOLDER = { id: 'all', company_name: 'All files', is_all: true }
const folderKey = (f) => (!f ? null : f.is_all ? 'all' : f.is_admin ? 'admin' : f.id)

const fmtBytes = (b) => {
  if (!b && b !== 0) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

// ── Upload modal (NS design, real per-file progress) ───────────────────────────
function MediaUploadModal({ folders, selectedFolder, onClose, onUploaded }) {
  const SHARED = '__shared'
  const clientFolders = (folders || []).filter(f => !f.is_admin && !f.is_all)
  const hasAdmin = (folders || []).some(f => f.is_admin)
  const folderOptions = [
    ...clientFolders.map(f => ({ value: f.id, label: f.company_name || f.name || 'Client' })),
    ...(hasAdmin ? [{ value: SHARED, label: 'Shared assets' }] : []),
  ]
  const initialDest = selectedFolder && !selectedFolder.is_all && !selectedFolder.is_admin
    ? selectedFolder.id
    : (folderOptions[0]?.value || SHARED)

  const [dest, setDest] = useState(initialDest)
  const [optimize, setOptimize] = useState(true)
  const [queue, setQueue] = useState([]) // { id, name, size, pct, status }
  const idRef = useRef(0)

  const addFiles = async (files) => {
    if (!files || !files.length) return
    const items = files.map(f => ({ id: ++idRef.current, name: f.name, size: f.size, pct: 0, status: 'uploading' }))
    const ids = items.map(i => i.id)
    setQueue(q => [...q, ...items])

    const fd = new FormData()
    files.forEach(f => fd.append('files', f))
    if (dest && dest !== SHARED) fd.append('client_id', dest)
    fd.append('optimize', optimize ? '1' : '0')

    try {
      const res = await apiService.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0
          setQueue(q => q.map(it => (ids.includes(it.id) ? { ...it, pct } : it)))
        },
      })
      setQueue(q => q.map(it => (ids.includes(it.id) ? { ...it, pct: 100, status: 'done' } : it)))
      toast.success(`${res.data?.files?.length || files.length} file(s) uploaded`)
      onUploaded()
    } catch (err) {
      setQueue(q => q.map(it => (ids.includes(it.id) ? { ...it, status: 'error' } : it)))
      toast.error(err.response?.data?.error || 'Upload failed')
    }
  }

  const done = queue.filter(f => f.status === 'done').length

  return (
    <NSModal
      icon="upload" tone="accent" onClose={onClose} width={620}
      title="Upload media" subtitle="Add files to the library. They're optimised automatically."
      foot={
        <>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {queue.length ? `${done} of ${queue.length} uploaded` : 'No files yet'}
          </span>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onClose}><I.check /> Done</button>
        </>
      }
    >
      <NSField label="Destination folder">
        <NSSelect value={dest} onChange={e => setDest(e.target.value)} options={folderOptions} />
      </NSField>

      <NSDrop icon="cloud" title="Drag & drop files, or click to browse" hint="Images, video, PDF · up to 200 MB per file" height={150} accent onFiles={addFiles} />

      {queue.length > 0 && (
        <div>
          <div className="drawer-section-label" style={{ marginBottom: 10 }}>Upload queue · {queue.length}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {queue.map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 11, background: 'var(--surface)' }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, flexShrink: 0, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-soft)' }}>
                  <I.file />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="truncate" style={{ fontSize: 12.5, fontWeight: 500 }}>{f.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{fmtBytes(f.size)}</span>
                  </div>
                  {f.status === 'done' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--success)', marginTop: 5 }}>
                      <I.check style={{ fontSize: 12 }} /> Uploaded
                    </div>
                  ) : f.status === 'error' ? (
                    <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 5 }}>Failed</div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <div className="progress" style={{ flex: 1, height: 5 }}><div style={{ width: `${f.pct}%` }} /></div>
                      <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', width: 32, textAlign: 'right' }}>{f.pct}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--text-soft)', cursor: 'pointer' }}>
        <NSToggle on={optimize} onChange={setOptimize} /> Optimise & generate responsive sizes on upload
      </label>
    </NSModal>
  )
}

// ── MediaLibrary ──────────────────────────────────────────────────────────────
const MediaLibrary = () => {
  const [selectedFolder, setSelectedFolder] = useState(ALL_FOLDER)
  const [fileSearch, setFileSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sortBy, setSortBy] = useState('date')
  const [viewMode, setViewMode] = useState('grid')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [detailFile, setDetailFile] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const queryClient = useQueryClient()

  // ── Queries (preserve all original API calls) ──────────────────────────────
  const { data: foldersData, isLoading: foldersLoading } = useQuery(
    ['media-folders'],
    () => apiService.get('/media/folders').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const folders = foldersData?.folders || []

  const { data: statsData } = useQuery(
    ['media-stats'],
    () => apiService.get('/media/stats').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const typeCount = (t) => (statsData?.typeStats || []).find(s => s.type === t)?.count || 0

  const { data: orphanedData } = useQuery(
    ['media-orphaned-folders'],
    () => apiService.get('/media/orphaned-folders').then(res => res.data),
    { staleTime: 30000 }
  )
  const orphanedFolders = orphanedData?.folders || []

  const folderId = selectedFolder?.is_admin ? 'admin' : selectedFolder?.id
  const { data: mediaData, isLoading: mediaLoading } = useQuery(
    ['folder-media', folderKey(selectedFolder), fileSearch, typeFilter],
    () => {
      const params = { page: 1, limit: 100, search: fileSearch, type: typeFilter }
      if (selectedFolder?.is_all) {
        return apiService.get('/media/', { params }).then(res => res.data)
      }
      return apiService.get(`/media/folder/${folderId}`, { params }).then(res => res.data)
    },
    { staleTime: 5 * 60 * 1000, enabled: !!selectedFolder, keepPreviousData: true }
  )
  const media = mediaData?.media || []

  // ── Mutations ──────────────────────────────────────────────────────────────
  const deleteOrphanedMutation = useMutation(
    (name) => apiService.delete(`/media/orphaned-folder/${encodeURIComponent(name)}`),
    {
      onSuccess: (_, name) => {
        toast.success(`Folder "${name}" deleted`)
        queryClient.invalidateQueries(['media-orphaned-folders'])
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete folder')
    }
  )

  const deleteClientFolderMutation = useMutation(
    (clientId) => apiService.delete(`/media/client-folder/${clientId}`),
    {
      onSuccess: (_, clientId) => {
        toast.success('Media folder deleted')
        queryClient.setQueryData(['media-folders'], (old) => {
          if (!old?.folders) return old
          return { ...old, folders: old.folders.filter(f => f.id !== clientId) }
        })
        if (selectedFolder?.id === clientId) setSelectedFolder(ALL_FOLDER)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete folder')
    }
  )

  const deleteFileMutation = useMutation(
    (id) => apiService.delete(`/media/${id}`),
    {
      onSuccess: () => {
        toast.success('File deleted')
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
        queryClient.invalidateQueries(['media-stats'])
        setDetailFile(null)
        setDeletingFileId(null)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed')
    }
  )

  const bulkDeleteMutation = useMutation(
    (fileIds) => Promise.all(fileIds.map(id => apiService.delete(`/media/${id}`))),
    {
      onSuccess: () => {
        toast.success(`${selectedFiles.length} file(s) deleted`)
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
        queryClient.invalidateQueries(['media-stats'])
        setSelectedFiles([])
        setShowDeleteConfirm(false)
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk delete failed')
    }
  )

  const invalidateMedia = () => {
    queryClient.invalidateQueries(['media-folders'])
    queryClient.invalidateQueries(['folder-media'])
    queryClient.invalidateQueries(['media-stats'])
  }

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleToggleSelect = (file) => {
    setSelectedFiles(prev => {
      const has = prev.some(f => f.id === file.id)
      return has ? prev.filter(f => f.id !== file.id) : [...prev, file]
    })
  }

  // Client folders (coloured avatars), admin/shared folder (folder icon), orphaned (red)
  const clientFolders = folders.filter(f => !f.is_admin)
  const adminFolder = folders.find(f => f.is_admin)

  const folderRows = [
    ...clientFolders.map((f, i) => ({ ...f, grad: (i % 6) + 1 })),
    ...(adminFolder ? [adminFolder] : []),
    ...orphanedFolders.map(f => ({
      id: null, company_name: f.name, media_count: f.count || 0, is_orphaned: true, _name: f.name
    }))
  ]

  // Type filters (rendered beneath folders, matching prototype)
  const typeFilters = [
    { label: 'Images', value: 'image', count: typeCount('image') },
    { label: 'Videos', value: 'video', count: typeCount('video') },
    { label: 'Documents', value: 'application', count: typeCount('other') },
    { label: 'Other', value: 'audio', count: typeCount('audio') },
  ]

  // Displayed files
  let displayedMedia = [...media]
  if (sortBy === 'name') displayedMedia.sort((a, b) => (a.original_name || '').localeCompare(b.original_name || ''))
  else if (sortBy === 'size') displayedMedia.sort((a, b) => (b.size || 0) - (a.size || 0))

  // Storage total
  const totalStorageGB = 10
  const usedStorageBytes = statsData?.totalSize ?? folders.reduce((s, f) => s + (f.storage_used || 0), 0)
  const usedStorageGB = usedStorageBytes / (1024 ** 3)
  const storagePercent = Math.min((usedStorageGB / totalStorageGB) * 100, 100)
  const totalFiles = statsData?.totalFiles ?? folders.reduce((s, f) => s + (f.media_count || 0), 0)

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Media</div>
          <h1>Media library</h1>
          <div className="subtitle">{totalFiles} files · {usedStorageGB.toFixed(1)} GB used of {totalStorageGB} GB</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn btn-primary" onClick={() => setUploadOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <IconUpload size={15} /> Upload
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      {uploadOpen && (
        <MediaUploadModal
          folders={folders}
          selectedFolder={selectedFolder}
          onClose={() => setUploadOpen(false)}
          onUploaded={invalidateMedia}
        />
      )}

      {/* File delete confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm && selectedFiles.length > 0}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedFiles.map(f => f.id))}
        isLoading={bulkDeleteMutation.isLoading}
        title="Delete Files"
        message={<>Delete <strong>{selectedFiles.length}</strong> file(s)? This cannot be undone.</>}
        confirmText="Delete Files"
      />
      <ConfirmationModal
        isOpen={!!deletingFileId}
        onClose={() => setDeletingFileId(null)}
        onConfirm={() => deleteFileMutation.mutate(deletingFileId)}
        isLoading={deleteFileMutation.isLoading}
        title="Delete File"
        message="Delete this file? This cannot be undone."
        confirmText="Delete File"
      />

      <div className="page-body">
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 14, alignItems: 'flex-start' }}>
          {/* ── Folder sidebar ── */}
          <div className="card card-pad-sm">
            <div className="eyebrow" style={{ padding: '4px 4px 8px' }}>Folders</div>

            {/* All files */}
            <button
              className={`layer-row ${selectedFolder?.is_all ? 'selected' : ''}`}
              style={{ width: '100%' }}
              onClick={() => { setSelectedFolder(ALL_FOLDER); setSelectedFiles([]); setFileSearch('') }}
            >
              <IconFolder size={14} color="var(--text-muted)" />
              <span style={{ flex: 1, textAlign: 'left' }}>All files</span>
              <span style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{totalFiles}</span>
            </button>

            {foldersLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}><LoadingSpinner size="sm" /></div>
            ) : (
              folderRows.map((f) => {
                const isSelected = folderKey(selectedFolder) === folderKey(f) && !f.is_orphaned && !f.is_all
                const isOrphaned = f.is_orphaned
                return (
                  <button
                    key={folderKey(f) || f.company_name}
                    className={`layer-row ${isSelected ? 'selected' : ''}`}
                    style={{ width: '100%' }}
                    onClick={() => {
                      if (isOrphaned) return
                      setSelectedFolder(f)
                      setSelectedFiles([])
                      setFileSearch('')
                    }}
                  >
                    {isOrphaned || f.is_admin ? (
                      <IconFolder size={14} color={isOrphaned ? '#ef4444' : 'var(--text-muted)'} />
                    ) : (
                      <span className={`avatar avatar-grad-${f.grad}`} style={{ width: 16, height: 16, fontSize: 8, flexShrink: 0 }} />
                    )}
                    <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isOrphaned ? '#ef4444' : undefined }}>
                      {f.company_name}
                      {isOrphaned && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>orphaned</span>}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{f.media_count}</span>
                  </button>
                )
              })
            )}

            {/* Filter */}
            <div className="divider" style={{ margin: '10px 0' }} />
            <div className="eyebrow" style={{ padding: '4px 4px 8px' }}>Filter</div>
            {typeFilters.map((t) => {
              const active = typeFilter === t.value
              return (
                <button
                  key={t.value}
                  className={`layer-row ${active ? 'selected' : ''}`}
                  style={{ width: '100%' }}
                  onClick={() => setTypeFilter(active ? '' : t.value)}
                >
                  <span style={{ width: 16 }} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{t.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{t.count}</span>
                </button>
              )
            })}

            {/* Storage */}
            <div className="divider" style={{ margin: '10px 0' }} />
            <div style={{ padding: '0 6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 6 }}>
                <span>Storage</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{usedStorageGB.toFixed(1)} / {totalStorageGB} GB</span>
              </div>
              <div className="progress thin"><div style={{ width: `${storagePercent}%` }} /></div>
            </div>
          </div>

          {/* ── File grid ── */}
          <div className="card card-pad-sm">
            <div className="filter-bar" style={{ border: 'none', padding: '6px 6px 12px' }}>
              <IconSearch size={16} />
              <input
                className="input"
                placeholder={`Search ${selectedFolder?.company_name || ''} files…`}
                value={fileSearch}
                onChange={e => setFileSearch(e.target.value)}
                style={{ flex: 1, padding: '4px 0', lineHeight: 1.4, height: 'auto' }}
              />
              <div className="segmented" style={{ marginLeft: 'auto' }}>
                {[['date', 'Recent'], ['name', 'Name'], ['size', 'Size']].map(([v, l]) => (
                  <button key={v} className={sortBy === v ? 'on' : ''} onClick={() => setSortBy(v)}>{l}</button>
                ))}
              </div>
              <div className="segmented">
                <button className={viewMode === 'grid' ? 'on' : ''} onClick={() => setViewMode('grid')} title="Grid view"><IconGrid /></button>
                <button className={viewMode === 'list' ? 'on' : ''} onClick={() => setViewMode('list')} title="List view"><IconList /></button>
              </div>
              {selectedFiles.length > 0 && (
                <button
                  className="btn btn-danger btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <IconTrash size={13} /> Delete {selectedFiles.length}
                </button>
              )}
            </div>

            {mediaLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><LoadingSpinner size="lg" /></div>
            ) : displayedMedia.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
                <span style={{ color: 'var(--text-faint)' }}><IconImage size={40} /></span>
                <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>
                  {fileSearch || typeFilter ? 'No files match your filters' : `No files in ${selectedFolder?.company_name} yet — upload some!`}
                </p>
                <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconUpload size={14} /> Upload files
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, padding: 4 }}>
                {displayedMedia.map((item) => (
                  <MediaTile
                    key={item.id}
                    item={item}
                    selected={selectedFiles.some(f => f.id === item.id)}
                    onOpen={() => setDetailFile(item)}
                    onToggle={() => handleToggleSelect(item)}
                  />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 4 }}>
                {displayedMedia.map((item) => (
                  <MediaRow
                    key={item.id}
                    item={item}
                    selected={selectedFiles.some(f => f.id === item.id)}
                    onOpen={() => setDetailFile(item)}
                    onToggle={() => handleToggleSelect(item)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Detail panel ── */}
      {detailFile && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.2)' }} onClick={() => setDetailFile(null)} />
          <div className="media-detail-panel">
            <div className="media-detail-header">
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {detailFile.original_name}
              </span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} onClick={() => setDetailFile(null)}>
                <IconX size={15} />
              </button>
            </div>
            <div className="media-detail-preview">
              {detailFile.mime_type?.startsWith('image/') ? (
                <img src={detailFile.public_url} alt={detailFile.alt_text || detailFile.original_name} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }} />
              ) : (
                <div style={{ color: 'var(--text-faint)', textAlign: 'center' }}>
                  {detailFile.mime_type?.startsWith('video/') ? <IconVideo size={48} /> : <IconFile size={48} />}
                </div>
              )}
            </div>
            <div className="media-detail-meta">
              {[
                ['Filename', detailFile.original_name],
                ['Type', detailFile.mime_type],
                ['Size', formatBytes(detailFile.size)],
                ['Uploaded', detailFile.created_at ? new Date(detailFile.created_at).toLocaleDateString() : '—'],
              ].map(([k, v]) => (
                <div key={k} className="media-meta-row">
                  <span className="media-meta-label">{k}</span>
                  <span className="media-meta-value" title={v}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 8, borderTop: '1px solid var(--border)' }}>
              <button
                className="tbl-action-btn"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                onClick={() => { navigator.clipboard?.writeText(detailFile.public_url); toast.success('Link copied!') }}
              >
                <IconLink /> Copy link
              </button>
              <button
                className="tbl-action-btn danger"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 12px' }}
                onClick={() => { setDeletingFileId(detailFile.id); setDetailFile(null) }}
              >
                <IconTrash /> Delete
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ── Media tile (grid) ───────────────────────────────────────────────────────
function MediaTile({ item, selected, onOpen, onToggle }) {
  const isImg = item.mime_type?.startsWith('image/')
  const isVid = item.mime_type?.startsWith('video/')
  const typeLabel = fileTypeLabel(item.mime_type)
  return (
    <div
      onClick={onOpen}
      style={{
        border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden',
        background: 'var(--bg-1)', cursor: 'pointer',
        transition: 'transform 0.15s ease, border-color 0.15s ease',
        outline: selected ? '2px solid var(--accent)' : 'none',
      }}
    >
      <div style={{ aspectRatio: '1', position: 'relative', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {isImg ? (
          <img src={item.thumbnail_url || item.public_url} alt={item.alt_text || item.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-soft)' }}>
            {isVid ? <IconVideo /> : <IconFile />}
          </div>
        )}
        <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(7, 8, 15, 0.6)', color: 'white', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{typeLabel}</span>
        <input
          type="checkbox"
          checked={selected}
          style={{ position: 'absolute', top: 7, left: 7, zIndex: 2, accentColor: 'var(--accent)', cursor: 'pointer' }}
          onClick={e => { e.stopPropagation(); onToggle() }}
          onChange={() => {}}
        />
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text)' }}>{item.original_name}</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{formatBytes(item.size)}</div>
      </div>
    </div>
  )
}

// ── Media row (list) ──────────────────────────────────────────────────────────
function MediaRow({ item, selected, onOpen, onToggle }) {
  const isImg = item.mime_type?.startsWith('image/')
  const isVid = item.mime_type?.startsWith('video/')
  const typeLabel = fileTypeLabel(item.mime_type)
  return (
    <div
      onClick={onOpen}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px',
        border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-1)',
        cursor: 'pointer', outline: selected ? '2px solid var(--accent)' : 'none',
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
        onClick={e => { e.stopPropagation(); onToggle() }}
        onChange={() => {}}
      />
      <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--text-soft)' }}>
        {isImg ? (
          <img src={item.thumbnail_url || item.public_url} alt={item.alt_text || item.original_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          isVid ? <IconVideo size={18} /> : <IconFile size={18} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text)' }}>{item.original_name}</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1, fontFamily: 'var(--font-mono)' }}>
          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
        </div>
      </div>
      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'var(--surface-2)', color: 'var(--text-soft)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{typeLabel}</span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', width: 64, textAlign: 'right', flexShrink: 0 }}>{formatBytes(item.size)}</span>
    </div>
  )
}

export default MediaLibrary
